import { useEffect } from 'react';

import type { Role } from 'src/__generated__/graphql';
import {
  PageSection,
  PageSectionState,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useProfileAnalytic } from 'src/hooks/useProfileAnalytic';

const TRACKING_INFO = {
  currentPageSection: PageSection.CAREER_HISTORY,
  pageTitle: 'profile+history',
  profileUpdateSource: 'steps',
  actionOrigin: 'steps:applicationForm',
};

export function useEditorTracking() {
  const trackingClient = useTrackingClient();
  const currentPage = useGetCurrentPage();

  useEffect(() => {
    trackingClient.trackView({
      ...TRACKING_INFO,
      currentPage,
      eventName: 'profile_section_displayed',
      view: currentPage,
      currentPageSectionState: PageSectionState.EDIT,
    });
  }, [currentPage, trackingClient]);

  return useTracking();
}

export function useTracking() {
  const trackingClient = useTrackingClient();
  const currentPage = useGetCurrentPage();

  // useProfileAnalytic call useProfile under the hood
  // reconsider if it's worth to fetch data just for tracking
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();

  // private, dont' return
  const trackLink = (info: Parameters<typeof trackingClient.trackLink>[0]) =>
    trackingClient.trackLink({
      profileApproachabilityStatus,
      profileSharedStatus,
      profileVisibilityLevel,
      currentPage,
      ...TRACKING_INFO,
      ...info,
    });

  const trackAddRole = (role: Role, nudgeType?: string) => {
    const eventName = nudgeType
      ? ['profile_update_succeeded', 'nudge_pressed']
      : ['profile_update_succeeded'];

    trackLink({
      link: 'profile-history-add-role',
      eventName,
      nudgeType,
      updateType: UpdateType.ADD,
      isDescriptionAdded: Boolean(role.achievements),
    });
  };

  const trackEditRole = (role: Role) => {
    trackLink({
      link: 'profile-history-edit-role',
      eventName: ['profile_update_succeeded'],
      updateType: UpdateType.EDIT,
      isDescriptionAdded: Boolean(role.achievements),
    });
  };

  const trackRejectRole = () => {
    trackLink({
      link: 'cv-parsing-nudge-career-history-dismiss',
      eventName: 'nudge_dismiss_pressed',
      nudgeType: 'cv parsing',
    });
  };

  const trackDeleteRole = () => {
    trackLink({
      link: 'profile_update_succeeded',
      eventName: ['profile_update_succeeded'],
      updateType: UpdateType.DELETE,
      isDescriptionAdded: false,
    });
  };

  return { trackAddRole, trackEditRole, trackRejectRole, trackDeleteRole };
}
